import { Spinner } from '@chakra-ui/react'
import { ItemIdentifierInfoModal as CoreItemIdentifierInfoModal } from 'core/components'
import { formatLocation, insertLocationRow } from 'core/components/modals/itemInfoLocation'
import {
  AttributeMappings,
  IdentifierEvents,
  ItemIdentifiers,
  ProductionOrderRow,
  TmrItem,
  TmrSimpleAttributesMap,
  WorkOrderRow
} from 'stylewhere/api'
import {
  Box,
  InfoBox,
  ItemAttributesCard,
  ItemEventBox,
  Modal,
  ModalTitle,
  ProductInfoBox,
  Spacer
} from 'stylewhere/components'
import { EncodingExtensions } from 'stylewhere/extensions'
import { T, __ } from 'stylewhere/i18n'
import { getCustomTranslation, getEncodingProductFields, getField } from 'stylewhere/shared/utils'

export class ItemIdentifierInfoModal extends CoreItemIdentifierInfoModal {
  state = {
    ...this.state,
    itemAttributes: undefined as TmrSimpleAttributesMap[] | undefined
  }

  init = async () => {
    const { identifier } = this.props
    try {
      const ret = await ItemIdentifiers.get(identifier.identifierId)

      const workOrderRow: WorkOrderRow | undefined = await this.getWorkOrderRow(identifier.workOrderRowId)
      const productionOrderRow: ProductionOrderRow | undefined = await this.getProductionOrder(
        identifier.productionOrderRowId
      )
      const item: TmrItem | undefined = await this.getItem(ret.itemId)

      const data = await IdentifierEvents.getIdentifierEvents(ret.id)

      let itemAttributes: TmrSimpleAttributesMap[] | undefined
      if (item) {
        const mappedAttrs = await AttributeMappings.getItemAttribute(item)
        const locationAttr: TmrSimpleAttributesMap = {
          label: __(T.misc.location),
          value: formatLocation(item.location)
        }
        itemAttributes = insertLocationRow(mappedAttrs, locationAttr)
      }

      this.setState({
        loader: false,
        identifier: ret,
        item,
        workOrderRow,
        productionOrderRow,
        events: data.content,
        itemAttributes
      } as any)
    } catch {
      this.setState({ loader: false } as any)
    }
  }

  getProductData = () => {
    // per Gucci il prodotto mostrato deve essere sempre quello dell'item (coerente con quanto
    // già mostrato fuori dalla modale), non quello della production/work order associata
    const { item, productionOrderRow, workOrderRow } = this.state
    return item?.product ?? productionOrderRow?.product ?? workOrderRow?.product
  }

  hasSizeMismatch = () => {
    // il prodotto dell'item (mostrato in modale) può differire da quello della production/work
    // order originariamente recuperata: se le taglie non coincidono avvisiamo l'utente
    const { item, productionOrderRow, workOrderRow } = this.state
    const itemProductSize = item?.product?.size?.value
    const orderProductSize = (productionOrderRow?.product ?? workOrderRow?.product)?.size?.value
    if (!itemProductSize || !orderProductSize) return false
    return itemProductSize !== orderProductSize
  }

  render() {
    const { onClose, visible } = this.props
    const { loader, identifier, workOrderRow, events, item, itemAttributes } = this.state
    const product = this.getProductData()
    // per Gucci la production order mostrata deve essere sempre quella dell'item (coerente con
    // quanto già mostrato fuori dal dettaglio), non quella storica associata all'identifier
    const productionOrderRow = item?.productionOrderRow
    const productionOrderDetails = productionOrderRow
      ? EncodingExtensions.getProductionOrderDetails({ productionOrderRow })
      : undefined
    const workOrder = workOrderRow?.workOrder
    const workOrderDetails = workOrderRow ? EncodingExtensions.getWorkOrderDetails({ workOrderRow }) : undefined
    const sizeMismatch = this.hasSizeMismatch()

    return (
      <Modal size="6xl" onClose={onClose} visible={visible} fullContent showCloseButton>
        <Box flex row>
          <Box flex={3} ml={35} mt={10} style={{ minWidth: 0 }}>
            {sizeMismatch && (
              <Box
                row
                mb={10}
                mr={35}
                p={12}
                vcenter
                style={{
                  backgroundColor: '#FFF4E5',
                  border: '1px solid #FFB74D',
                  borderRadius: 8,
                  color: '#7A4A00',
                  fontSize: 14,
                  fontWeight: 'bold'
                }}
                data-testid="size-mismatch-warning"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginRight: 10 }}>
                  <path
                    d="M12 3L22 20H2L12 3Z"
                    fill="#FFB74D"
                    stroke="#7A4A00"
                    strokeWidth="1"
                    strokeLinejoin="round"
                  />
                  <rect x="11" y="9" width="2" height="6" rx="1" fill="#7A4A00" />
                  <rect x="11" y="16" width="2" height="2" rx="1" fill="#7A4A00" />
                </svg>
                {__('misc.size_mismatch_warning')}
              </Box>
            )}
            <ModalTitle
              title={__(T.misc.item_identifier_info)}
              data-testid={`identifiers-modal-title`}
              style={{ flex: 0, paddingTop: 20, paddingBottom: 15 }}
            />
            <Box
              flex
              ph={35}
              pb={0}
              pr={0}
              pl={0}
              mr={35}
              mb={20}
              style={{
                height: 'calc(80vh - 40px)',
                maxHeight: 'calc(80vh - 40px)',
                overflow: 'auto',
                overflowX: 'hidden'
              }}
            >
              {!loader && identifier && (
                <>
                  <Box p={20} pb={5} m={2} mt={5} boxShadow={'0px 1px 4px rgba(0, 0, 0, 0.15)'} borderRadius={10}>
                    {this.fields.map((field, index) => {
                      return (
                        <ProductInfoBox.Field
                          key={`${index}-${field.label}`}
                          label={getCustomTranslation(field.label)}
                          rowLabel={field.label}
                          value={getField(identifier, field.path) ?? '---'}
                        />
                      )
                    })}
                  </Box>
                  {item && <ItemAttributesCard attributes={itemAttributes} labels={item.labels} />}
                  <Spacer height="10" />
                  {productionOrderRow && (
                    <Box mb={10}>
                      <InfoBox
                        title={__(T.misc.production_order)}
                        subtitle={productionOrderDetails?.subtitle}
                        data={productionOrderDetails?.data}
                        fields={EncodingExtensions.getProductionOrderFields()}
                      />
                    </Box>
                  )}
                  {product && (
                    <ProductInfoBox
                      paddingCard={'20px 20px 20px 20px'}
                      shadowCard={'0px 1px 4px rgba(0, 0, 0, 0.15)'}
                      marginCard={'2px'}
                      product={product}
                      fields={getEncodingProductFields()}
                    />
                  )}
                  {workOrder && (
                    <Box mt={10}>
                      <InfoBox
                        title={__(T.misc.work_order)}
                        subtitle={workOrderDetails?.subtitle}
                        data={workOrderDetails?.data}
                        fields={[]}
                      />
                    </Box>
                  )}
                </>
              )}
              {loader && (
                <Box mt={20} center>
                  <Spinner color="#000" size="xl" />
                </Box>
              )}
            </Box>
          </Box>
          <ItemEventBox title={__(T.misc.item_identifier_events)} events={events} />
        </Box>
      </Modal>
    )
  }
}
